import './App.css';
import {useCallback, useEffect, useState} from 'react';
import {Routes, Route, Navigate, useNavigate, useSearchParams} from 'react-router-dom';
import {apiFetch, getStoredUsername, setStoredUsername, setStoredUserId} from './api';
import Header from './components/Header';
import Footer from './components/Footer';
import {ToastProvider} from './components/Toast';
import LandingPage from './components/LandingPage';
import LoginScreen from './components/LoginScreen';
import RegisterScreen from './components/RegisterScreen';
import ForgotPasswordScreen from './components/ForgotPasswordScreen';
import ResetPasswordScreen from './components/ResetPasswordScreen';
import ChecklistContainer from './components/ChecklistContainer';
import AccountSettingsPage from './components/AccountSettingsPage';
import Spinner from './components/Spinner';
import ErrorBoundary from './components/ErrorBoundary';
import SharedChecklistPage from './components/SharedChecklistPage';

function LegacyRedirect() {
    const [params] = useSearchParams();
    const verified = params.get('verified');
    const reset = params.get('reset');
    if (verified) return <Navigate to={`/signin?verified=${verified}`} replace />;
    if (reset) return <Navigate to={`/signin?reset=${reset}`} replace />;
    return null;
}

function PublicOnly({isAuthenticated, children}) {
    if (isAuthenticated) return <Navigate to="/" replace />;
    return children;
}

function ProtectedOnly({isAuthenticated, children}) {
    if (!isAuthenticated) return <Navigate to="/signin" replace />;
    return children;
}

export default function App() {
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
    const [isAuthenticated, setIsAuthenticated] = useState(null); // null = loading
    const [username, setUsername] = useState(getStoredUsername);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentViewLabel, setCurrentViewLabel] = useState('All');
    const navigate = useNavigate();

    const toggleSidebar = useCallback(() => setSidebarOpen(prev => !prev), []);

    const toggleTheme = useCallback(
        () =>
            setTheme(t => {
                const next = t === 'dark' ? 'light' : t === 'light' ? 'minimal' : t === 'minimal' ? 'github' : 'dark';
                localStorage.setItem('theme', next);
                return next;
            }),
        [],
    );

    // Check session on mount via /auth/me
    useEffect(() => {
        let cancelled = false;
        apiFetch('/auth/me')
            .then(res => res.json())
            .then(data => {
                if (cancelled) return;
                setUsername(data.username);
                setStoredUsername(data.username);
                setIsAuthenticated(true);
            })
            .catch(() => {
                if (!cancelled) setIsAuthenticated(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const handleAuthLogout = () => {
            setStoredUsername(null);
            setStoredUserId(null);
            setIsAuthenticated(false);
            setUsername('');
        };
        window.addEventListener('auth:logout', handleAuthLogout);
        return () => window.removeEventListener('auth:logout', handleAuthLogout);
    }, []);

    const handleLogin = useCallback(
        (loggedInUsername, userId) => {
            setStoredUsername(loggedInUsername);
            setStoredUserId(userId);
            setUsername(loggedInUsername);
            setIsAuthenticated(true);
            navigate('/');
        },
        [navigate],
    );

    const handleLogout = useCallback(async () => {
        try {
            await apiFetch('/auth/logout', {method: 'POST'});
        } catch {}
        setStoredUsername(null);
        setStoredUserId(null);
        setIsAuthenticated(false);
        setUsername('');
        navigate('/');
    }, [navigate]);

    const handleDeleteAccount = useCallback(async () => {
        await apiFetch('/users/me', {method: 'DELETE'});
        setStoredUsername(null);
        setStoredUserId(null);
        setIsAuthenticated(false);
        setUsername('');
        navigate('/');
    }, [navigate]);

    if (isAuthenticated === null) {
        return <Spinner size={32} className="spinner-fullpage" />;
    }

    const authShell = content => (
        <div className="App" data-theme={theme}>
            <ToastProvider>
                <Header theme={theme} onToggleTheme={toggleTheme} onLogoClick={() => navigate('/')} />
                <ErrorBoundary>{content}</ErrorBoundary>
                <Footer />
            </ToastProvider>
        </div>
    );

    return (
        <Routes>
            <Route
                path="/signin"
                element={
                    <PublicOnly isAuthenticated={isAuthenticated}>
                        {authShell(<LoginScreen onLogin={handleLogin} />)}
                    </PublicOnly>
                }
            />
            <Route
                path="/register"
                element={<PublicOnly isAuthenticated={isAuthenticated}>{authShell(<RegisterScreen />)}</PublicOnly>}
            />
            <Route
                path="/forgot-password"
                element={
                    <PublicOnly isAuthenticated={isAuthenticated}>{authShell(<ForgotPasswordScreen />)}</PublicOnly>
                }
            />
            <Route
                path="/reset-password"
                element={
                    <PublicOnly isAuthenticated={isAuthenticated}>{authShell(<ResetPasswordScreen />)}</PublicOnly>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedOnly isAuthenticated={isAuthenticated}>
                        <div className="App" data-theme={theme}>
                            <ToastProvider>
                                <Header
                                    theme={theme}
                                    onToggleTheme={toggleTheme}
                                    onLogout={handleLogout}
                                    username={username}
                                    onAccountSettings={() => navigate('/settings')}
                                    onLogoClick={() => navigate('/')}
                                    onToggleSidebar={toggleSidebar}
                                    sidebarOpen={sidebarOpen}
                                    currentViewLabel={currentViewLabel}
                                />
                                <ErrorBoundary>
                                    <AccountSettingsPage
                                        username={username}
                                        onDeleteAccount={handleDeleteAccount}
                                        onBack={() => navigate('/')}
                                    />
                                </ErrorBoundary>
                                <Footer />
                            </ToastProvider>
                        </div>
                    </ProtectedOnly>
                }
            />
            <Route
                path="/"
                element={
                    isAuthenticated ? (
                        <div className="App" data-theme={theme}>
                            <ToastProvider>
                                <Header
                                    theme={theme}
                                    onToggleTheme={toggleTheme}
                                    onLogout={handleLogout}
                                    username={username}
                                    onAccountSettings={() => navigate('/settings')}
                                    onLogoClick={() => navigate('/')}
                                    onToggleSidebar={toggleSidebar}
                                    sidebarOpen={sidebarOpen}
                                    currentViewLabel={currentViewLabel}
                                />
                                <ErrorBoundary>
                                    <ChecklistContainer
                                        sidebarOpen={sidebarOpen}
                                        onViewLabelChange={setCurrentViewLabel}
                                    />
                                </ErrorBoundary>
                                <Footer />
                            </ToastProvider>
                        </div>
                    ) : (
                        <>
                            <LegacyRedirect />
                            <LandingPage theme={theme} onToggleTheme={toggleTheme} />
                        </>
                    )
                }
            />
            <Route path="/s/:token" element={<SharedChecklistPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}
